import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { Block, BlockAction } from "../contract/types";
import { normalizeAssessmentCopy } from "../lib/assessment";
import { Icon, type IconName } from "../components/Icon";
import { cssVars } from "../lib/css";
import { getBlockComponent } from "./registry";
import { Unknown } from "./Unknown";
import { BLOCK_EVENTS } from "./useBlockAction";
import type { BlockEventSink, BlockViewProps, EmitBlockEvent } from "./types";

/**
 * Envoltura común de todo átomo: resuelve `meta` (título, subtítulo, ancho,
 * plegado) y las `actions` del envelope, liga el `block_id` a los eventos y
 * delega el cuerpo en el componente registrado. Un `type` desconocido cae en
 * `Unknown` sin romper el stream.
 */

export interface BlockRendererProps {
  block: Block;
  emit: BlockEventSink;
  interactive?: boolean;
  onNavigate?: BlockViewProps["onNavigate"];
}

/**
 * Átomos que titulan por su cuenta: en `callout` manda la prop propia `title`
 * y `meta.title` solo entra como respaldo, para no pintar el encabezado dos
 * veces.
 */
const SELF_TITLED: ReadonlySet<string> = new Set(["callout"]);

const ACTION_ICON: Record<BlockAction["kind"], IconName> = {
  submit: "arrow-right",
  select: "target",
  toggle: "check",
  navigate: "external",
};

function BlockFrame({
  block,
  emit,
  interactive,
  children,
}: {
  block: Block;
  emit: EmitBlockEvent;
  interactive: boolean;
  children: ReactNode;
}) {
  const meta = block.meta ?? {};
  const actions = block.type === "quiz" ? [] : block.actions ?? [];
  const isSourceList = block.type === "source_list";
  const collapsible = isSourceList || meta.collapsible === true;
  const [open, setOpen] = useState(!isSourceList && meta.collapsed !== true);

  const ownTitle = SELF_TITLED.has(block.type);
  const title = isSourceList ? "Fuentes" : ownTitle ? null : meta.title;
  const subtitle = meta.subtitle;
  const hasHeader = Boolean(title || subtitle || collapsible);
  const width = meta.width ?? "full";

  function invoke(action: BlockAction) {
    if (!interactive) return;
    emit(BLOCK_EVENTS.actionInvoke, { action_id: action.id, label: action.label, action_event: action.event });
  }

  return (
    <div
      className="block"
      data-block-type={block.type}
      data-width={width}
      data-open={collapsible ? String(open) : undefined}
      id={`block-${block.id}`}
      style={cssVars({ "--block-id": block.id })}
    >
      {hasHeader ? (
        <header className="block__header">
          <div className="block__heading">
            {title ? <h3 className="block__title">{title}</h3> : null}
            {subtitle ? <p className="block__subtitle">{subtitle}</p> : null}
          </div>
          {collapsible ? (
            <button
              type="button"
              className="block__collapse u-button u-button--ghost u-button--small"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
            >
              <Icon name={open ? "chevron-down" : "chevron-right"} size="0.75rem" />
              {isSourceList ? (open ? "Ocultar fuentes" : "Ver fuentes") : open ? "Ocultar" : "Mostrar"}
            </button>
          ) : null}
        </header>
      ) : null}

      {open ? <div className="block__body">{children}</div> : null}

      {actions.length > 0 && open ? (
        <footer className="block__actions">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              className={`u-button ${action.kind === "submit" ? "u-button--primary" : ""}`}
              disabled={!interactive}
              onClick={() => invoke(action)}
            >
              <Icon name={ACTION_ICON[action.kind] ?? "arrow-right"} size="0.75rem" />
              {action.label}
            </button>
          ))}
        </footer>
      ) : null}
    </div>
  );
}

export function BlockRenderer({
  block: sourceBlock,
  emit: sink,
  interactive = true,
  onNavigate,
}: BlockRendererProps) {
  const block = useMemo(() => normalizeAssessmentCopy(sourceBlock), [sourceBlock]);
  const Component = getBlockComponent(block.type);

  // El `block_id` se liga aquí, una sola vez: los átomos emiten eventos y el
  // renderer les pone el bloque al que pertenecen.
  const emit = useCallback<EmitBlockEvent>(
    (event, payload) => sink(block.id, event, payload),
    [sink, block.id],
  );

  return (
    <BlockFrame block={block} emit={emit} interactive={interactive}>
      {Component ? (
        <Component
          block={block}
          emit={emit}
          interactive={interactive}
          onNavigate={onNavigate}
        />
      ) : (
        <Unknown block={block} emit={emit} interactive={interactive} onNavigate={onNavigate} />
      )}
    </BlockFrame>
  );
}

export default BlockRenderer;
